import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// pushDevices — système de notifications interne du PWA.
//
// Principe : lorsqu'un utilisateur installe l'application (ou passe par la
// procédure iPhone), on enregistre son appareil en base avec un CODE UNIQUE
// dérivé de son compte + de l'identifiant unique du navigateur (empreinte
// stable conservée dans le localStorage). Ce code sert de « canal » interne
// pour pousser les notifications vers l'appareil, sans dépendre d'un
// prestataire externe.
//
// Le PWA interroge ensuite régulièrement les nouvelles notifications liées à
// l'utilisateur, les affiche (notification système, façon WhatsApp) et met à
// jour le badge du nombre de notifications sur l'icône de l'application.
// ─────────────────────────────────────────────────────────────

const BROWSER_ID_KEY = 'zifek-browser-id';

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return (crypto as Crypto).randomUUID();
    }
  } catch {
    /* fallback ci-dessous */
  }
  return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Identifiant unique et stable du navigateur/appareil (empreinte).
 * Généré une seule fois puis conservé localement.
 */
export function getBrowserId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let id = localStorage.getItem(BROWSER_ID_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(BROWSER_ID_KEY, id);
    }
    return id;
  } catch {
    return randomId();
  }
}

/** Plateforme de l'appareil (utile pour cibler / diagnostiquer). */
export function detectPlatform(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh/i.test(ua)) return 'mac';
  if (/Linux/i.test(ua)) return 'linux';
  return 'web';
}

/** Code unique du canal : compte + empreinte navigateur. */
export function buildDeviceCode(userId: number | string, browserId: string): string {
  return `${userId}:${browserId}`;
}

export interface PushDevice {
  id: number;
  user_id: number;
  device_code: string;
  browser_id: string | null;
  platform: string | null;
  notifications_enabled: boolean | null;
  badge_count: number | null;
  last_seen: string | null;
  created_at: string | null;
}

export interface RegisterDeviceInput {
  userId: number | string;
  accountType?: string | null;
  notificationsEnabled?: boolean;
}

/**
 * Enregistre (ou met à jour) l'appareil courant pour cet utilisateur.
 * Idempotent : la contrainte d'unicité sur `device_code` évite les doublons.
 */
export async function registerPushDevice(input: RegisterDeviceInput): Promise<PushDevice | null> {
  const userId = Number(input.userId);
  if (!Number.isFinite(userId) || userId <= 0) return null;
  const browserId = getBrowserId();
  if (!browserId) return null;
  const deviceCode = buildDeviceCode(userId, browserId);

  const payload = {
    user_id: userId,
    device_code: deviceCode,
    browser_id: browserId,
    platform: detectPlatform(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 400) : null,
    account_type: input.accountType ?? null,
    notifications_enabled: input.notificationsEnabled ?? true,
    last_seen: new Date().toISOString(),
  };

  try {
    const { data, error } = await supabase
      .from('push_devices')
      .upsert(payload, { onConflict: 'device_code' })
      .select('*')
      .maybeSingle();
    if (error) throw error;
    return (data as PushDevice) || null;
  } catch {
    return null;
  }
}

/** Met à jour le badge / la date de dernière activité de l'appareil. */
export async function touchPushDevice(
  userId: number | string,
  patch: { badgeCount?: number; notificationsEnabled?: boolean } = {},
): Promise<void> {
  const userIdNum = Number(userId);
  if (!Number.isFinite(userIdNum) || userIdNum <= 0) return;
  const browserId = getBrowserId();
  if (!browserId) return;
  const deviceCode = buildDeviceCode(userIdNum, browserId);
  const payload: Record<string, unknown> = { last_seen: new Date().toISOString() };
  if (patch.badgeCount !== undefined) payload.badge_count = patch.badgeCount;
  if (patch.notificationsEnabled !== undefined) payload.notifications_enabled = patch.notificationsEnabled;
  try {
    await supabase.from('push_devices').update(payload).eq('device_code', deviceCode);
  } catch {
    /* best-effort */
  }
}

export interface NotifRow {
  id: number;
  type: string | null;
  message: string | null;
  is_read: number | null;
  created_at: string | null;
}

export interface CountUnreadOptions {
  /**
   * Exclut les notifications de type « message » du comptage.
   * Les messages privés sont comptés séparément (table `messages`) afin
   * d'éviter un double comptage dans le badge de l'icône.
   */
  excludeMessages?: boolean;
}

/** Nombre de notifications non lues pour l'utilisateur (badge de l'icône). */
export async function countUnreadNotifications(
  userId: number | string,
  options: CountUnreadOptions = {},
): Promise<number> {
  const userIdNum = Number(userId);
  if (!Number.isFinite(userIdNum) || userIdNum <= 0) return 0;
  try {
    let query = supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userIdNum)
      .eq('is_read', 0);
    if (options.excludeMessages) query = query.neq('type', 'message');
    const { count, error } = await query;
    if (error) throw error;
    return count ?? 0;
  } catch {
    return 0;
  }
}

/** Nombre de messages privés non lus reçus par l'utilisateur (messagerie). */
export async function countUnreadMessages(userId: number | string): Promise<number> {
  const userIdNum = Number(userId);
  if (!Number.isFinite(userIdNum) || userIdNum <= 0) return 0;
  try {
    const { count, error } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('receiver_id', userIdNum)
      .or('is_read.eq.0,is_read.is.null');
    if (error) throw error;
    return count ?? 0;
  } catch {
    return 0;
  }
}

/**
 * Nombre total à afficher sur l'icône de l'application (badge façon WhatsApp) :
 * notifications non lues (hors messages pour éviter le double comptage)
 * + messages privés non lus.
 */
export async function computeAppBadgeCount(userId: number | string): Promise<number> {
  const [notifCount, msgCount] = await Promise.all([
    countUnreadNotifications(userId, { excludeMessages: true }),
    countUnreadMessages(userId),
  ]);
  return notifCount + msgCount;
}

/** Récupère les notifications les plus récentes (pour l'affichage système). */
export async function fetchLatestNotifications(userId: number | string, limit = 5): Promise<NotifRow[]> {
  const userIdNum = Number(userId);
  if (!Number.isFinite(userIdNum) || userIdNum <= 0) return [];
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('id, type, message, is_read, created_at')
      .eq('user_id', userIdNum)
      .eq('is_read', 0)
      .order('id', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data || []) as NotifRow[];
  } catch {
    return [];
  }
}

// ── Badge de l'icône de l'application (Badging API) ──
// Affiche le nombre de notifications sur l'icône du PWA (Android / desktop).
export function supportsAppBadge(): boolean {
  return typeof navigator !== 'undefined' && 'setAppBadge' in navigator;
}

export async function setAppBadge(count: number): Promise<void> {
  if (!supportsAppBadge()) return;
  try {
    const nav = navigator as Navigator & {
      setAppBadge?: (n?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (count > 0) await nav.setAppBadge?.(count);
    else await nav.clearAppBadge?.();
  } catch {
    /* non supporté : on ignore */
  }
}

/** Libellé lisible par type de notification (titre de la notification système). */
export function notificationTitle(type: string | null | undefined, fallback = 'Zifek'): string {
  switch (type) {
    case 'order': return 'Nouvelle commande';
    case 'payment': return 'Paiement reçu';
    case 'message': return 'Nouveau message';
    case 'contact': return 'Nouveau message de contact';
    case 'booking': return 'Rendez-vous';
    case 'vendor': return 'Nouveau vendeur';
    case 'stock': return 'Alerte stock';
    case 'mission': return 'Mission';
    case 'finance': return 'Alerte financière';
    case 'form': return 'Nouveau formulaire rempli';
    case 'store_transfer': return 'Transfert reçu';
    case 'loyalty_points': return 'Points de fidélité';
    default: return fallback;
  }
}

/**
 * Chemin interne vers lequel ouvrir l'application quand on clique sur une
 * notification. Chaque type mène à SA page réelle (commandes, messages,
 * réservations, stock…) au lieu de retomber sur le tableau de bord.
 */
export function notificationTargetPath(
  type: string | null | undefined,
  options: { publicationId?: number | null } = {},
): string {
  const id = Number(options.publicationId) || 0;
  switch (type) {
    case 'order':
    case 'payment':
      return id > 0 ? `/dashboard/orders?order=${id}` : '/dashboard/orders';
    case 'payment_failed':
      return '/dashboard/abonnement';
    case 'besoin':
      return '/dashboard/besoins';
    case 'mission':
      return '/missions';
    case 'message':
      return '/dashboard/messages';
    case 'contact':
      return '/dashboard/apps/customer-support';
    case 'booking':
      return '/dashboard/apps/zcalendar';
    case 'stock':
      return '/dashboard/gestion-stock';
    case 'finance':
      return '/dashboard/finance';
    case 'form':
      return '/dashboard/apps/forms/responses';
    case 'vendor':
      return '/dashboard/marketplace';
    case 'store_transfer':
    case 'money_request':
      return '/dashboard/wallet';
    case 'loyalty_points':
      return '/dashboard/apps/ma-fidelite';
    default:
      return '/dashboard';
  }
}

/**
 * URL ABSOLUE de la cible (nécessaire pour OneSignal et le service worker).
 * Retombe sur le chemin relatif hors navigateur.
 */
export function notificationTargetUrl(
  type: string | null | undefined,
  options: { publicationId?: number | null } = {},
): string {
  const path = notificationTargetPath(type, options);
  if (typeof window === 'undefined') return path;
  // Le PWA peut être servi sous un chemin de base (sous-domaine / préfixe).
  // On préfixe donc le chemin pour que le clic ouvre la bonne page DANS
  // l'application installée, sans retomber sur une 404.
  const base = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');
  return `${window.location.origin}${base}${path}`;
}